import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';

import { API_BASE_URL } from '../config/config';
import {
  ApiResponse_boolean,
  ApiResponse_memberProfileDTO,
  ApiResponse_string,
  MemberProfileClient,
  MemberProfileDTO,
  MemberProfileUpdateDTO,
} from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';

const apiClient = new MemberProfileClient(API_BASE_URL, axiosInstance);

export const MY_PROFILE_KEY = ['myProfile'];

// The signed-in member's own profile (/member/profile). Every route resolves the member from the
// token, so nothing here sends a member id.
export const useMyProfile = (): UseQueryResult<MemberProfileDTO | null> =>
  useQuery<MemberProfileDTO | null>({
    queryKey: MY_PROFILE_KEY,
    queryFn: async () => {
      const response = await apiClient.getMyProfile();
      return response?.data ?? null;
    },
    // Short, unlike the course lists: the picture link inside it is a SAS URL that expires after
    // an hour.
    staleTime: 60 * 1000,
  });

export const useUpdateMyProfile = (): UseMutationResult<
  ApiResponse_memberProfileDTO,
  unknown,
  MemberProfileUpdateDTO
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update: MemberProfileUpdateDTO) =>
      apiClient.updateMyProfile(update),
    onSuccess: (response) => {
      if (response?.data) {
        queryClient.setQueryData(MY_PROFILE_KEY, response.data);
      }
    },
  });
};

export const useUploadMyProfilePicture = (): UseMutationResult<
  ApiResponse_string,
  unknown,
  File
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) =>
      apiClient.uploadMyProfilePicture(file.name, file),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: MY_PROFILE_KEY });
    },
  });
};

export const useSetMyEmailPreference = (): UseMutationResult<
  ApiResponse_boolean,
  unknown,
  boolean
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (receiveEmails: boolean) =>
      apiClient.setMyEmailPreference({ receiveEmails }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: MY_PROFILE_KEY });
    },
  });
};
